import React, { useEffect, useState } from "react";
import { Kanban, DollarSign } from "lucide-react";
import { fetchPipelineSummary } from "../lib/api";
import { STAGES } from "../lib/stages";

function formatMoney(n) {
  if (!n) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
  return `$${n.toLocaleString()}`;
}

/**
 * Compact Dashboard tile that shows the breakdown of dispensaries
 * across the 8 pipeline stages as a single horizontal stacked bar.
 * Hovering a segment surfaces the count for that stage.
 *
 * Designed to live in a 1-column-wide spot — no kanban-scroll problems.
 */
export function PipelineGlanceCard() {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    fetchPipelineSummary()
      .then(setSummary)
      .catch(() => setSummary(null));
  }, []);

  if (!summary) {
    return (
      <div data-testid="pipeline-glance-card" className="panel p-5 h-[200px] animate-pulse" />
    );
  }

  const total = summary.total || 0;
  const totalVal = summary.total_value_estimate || 0;

  // Build the segments in stage order.
  const segments = STAGES.map((s) => ({
    ...s,
    count: summary.by_stage[s.value] || 0,
  }));

  return (
    <div data-testid="pipeline-glance-card" className="panel p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-mono">
          <Kanban className="w-3 h-3 text-emerald-300" />
          Pipeline at a glance
        </div>
        {totalVal > 0 && (
          <div className="flex items-center gap-1 text-[10px] font-mono text-amber-300">
            <DollarSign className="w-3 h-3" />
            {formatMoney(totalVal)}/mo est.
          </div>
        )}
      </div>

      {/* Stacked bar */}
      <div
        className="flex w-full h-2 rounded-full overflow-hidden bg-zinc-900"
        role="img"
        aria-label="Pipeline stage breakdown"
      >
        {segments.map((s) => {
          const pct = total > 0 ? (s.count / total) * 100 : 0;
          if (pct === 0) return null;
          return (
            <div
              key={s.value}
              data-testid={`pipeline-glance-bar-${s.value}`}
              className={`${s.bar} transition-all`}
              style={{ width: `${pct}%` }}
              title={`${s.label} · ${s.count.toLocaleString()}`}
            />
          );
        })}
      </div>

      {/* Legend grid */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
        {segments.map((s) => (
          <div
            key={s.value}
            data-testid={`pipeline-glance-row-${s.value}`}
            className="flex items-center gap-2"
          >
            <span className={`w-1.5 h-1.5 rounded-full ${s.bar} shrink-0`} />
            <span className={`${s.color} truncate`}>{s.label}</span>
            <span className="ml-auto font-mono text-zinc-400">
              {s.count.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PipelineGlanceCard;
